import { Power2 } from 'gsap';
import * as PIXI from 'pixi.js';

import DraggablePreset from './DraggablePreset';
import Text from '../../graphics/Text';
import gradient from '../../graphics/gradient';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getCenterX,
  getCenterY,
  getFixBottomY,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 2;

export default class GradientBox extends DraggablePreset {
  gradientBox = {
    alpha: 1,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    lineThickness: 0,
    texture: null,
    draw: (graphics) => {
      if (
        !this.gradientSprite.texture ||
        this.gradientBox.height < this.gradientBox.lineThickness
      ) {
        return;
      }

      graphics.lineTextureStyle({
        alignment: 0,
        alpha: this.gradientBox.alpha,
        width: this.gradientBox.lineThickness,
        texture: this.gradientSprite.texture,
      });

      // TODO: The canvas version is slightly taller than the webgl version [SSR]

      graphics.drawRect(
        0,
        this.gradientBox.lineThickness, // It seems it needs to be offset
        this.gradientBox.width,
        this.gradientBox.height
      );

      // this is brittle, but textures start at the origin and can't be moved
      // so we have to move the graphics object relative to its parent instead
      graphics.position.set(
        this.gradientBox.x,
        this.gradientBox.y - this.gradientBox.lineThickness
      );
    },
  };

  constructor(app) {
    super(app);

    this.topTextMask = new GraphicMask();
    this.topText = new Text(app);
    this.topText.mask = this.topTextMask.mask;

    this.bottomTextMask = new GraphicMask();
    this.bottomText = new Text(app);
    this.bottomText.mask = this.bottomTextMask.mask;

    this.zoomableContainer = new PIXI.Container();
    this.zoomedGraphics = new PIXI.Graphics();

    this.zoomableContainer.addChild(
      this.zoomedGraphics,
      this.topText,
      this.bottomText,
      this.topTextMask.mask,
      this.bottomTextMask.mask
    );
    this.container.addChild(this.zoomableContainer);
  }

  update(model) {
    model = {
      ...model,
      dropShadow: NONE, // disable drop shadow for this preset
      anchor: { x: 0.5, y: 0.5 }, // center anchor because it should grow outwardly. NOTE: this means scaling happens from center.
    };
    super.update(model);
    const halfDuration = Math.min(this.duration, MAX_DURATION) / 2;
    const zoomDuration = halfDuration / 3;
    const revealDuration = halfDuration - zoomDuration;
    const textDelay = revealDuration / 4;

    this.topText.text = model.text;
    this.topText.updateStyle(model);
    this.setDraggableRoot(
      this.topText.lineControls.height,
      this.topText.lineControls.width
    );
    this.topText.lineControls.x = getCenterX(
      this.topText.lineControls,
      this.root
    );
    this.topText.lineControls.y = getCenterY(
      this.topText.lineControls,
      this.root
    );

    // bottom text is an identical copy of top text
    // but will have a different mask and different position
    this.bottomText.text = model.text;
    this.bottomText.updateStyle(model);
    this.bottomText.lineControls.x = this.topText.lineControls.x;
    this.bottomText.lineControls.y = this.topText.lineControls.y;

    // position masks
    this.topTextMask.width = this.topText.innerControls.width;
    this.topTextMask.height = this.topText.innerControls.height / 2;
    this.topTextMask.x = this.topText.innerControls.x;
    this.topTextMask.y = this.topText.innerControls.y;

    this.bottomTextMask.width = this.bottomText.innerControls.width;
    this.bottomTextMask.height = this.bottomText.innerControls.height / 2;
    this.bottomTextMask.x = this.bottomText.innerControls.x;
    this.bottomTextMask.y = getFixBottomY(
      this.bottomTextMask,
      this.bottomText.innerControls
    );

    // animate gradient box
    const lineThickness = this.topText.fontSize / 3;
    const boxHeight = this.topText.innerControls.height + lineThickness * 5;
    this.gradientBox.width =
      this.topText.innerControls.width + lineThickness * 5;
    this.gradientBox.height = 0;
    this.gradientBox.x = getCenterX(
      this.gradientBox,
      this.topText.innerControls
    );
    this.gradientBox.y = getCenterY(
      this.gradientBox,
      this.topText.innerControls
    );
    this.gradientBox.alpha = 0;
    this.gradientBox.lineThickness = lineThickness;
    const texture = gradient(
      this.secondaryColor.clone().spin(45).lighten(25).toHexString(),
      this.secondaryColor.toHexString(),
      this.root.height * 2.5,
      this.root.width
    );
    this.gradientSprite = PIXI.Sprite.from(texture);

    this.animate(this.gradientBox)
      .to({
        alpha: this.secondaryColor.getAlpha(),
        height: boxHeight,
        y: `-=${boxHeight / 2}`,
        duration: revealDuration,
        ease: Power2.easeOut,
      })
      .reverseOut();

    // animate text
    this.topText.innerControls.y += boxHeight / 2;
    this.topText.alpha = 0;
    this.animate(this.topText.innerControls)
      .to(
        {
          alpha: this.primaryColor.getAlpha(),
          y: `-=${boxHeight / 2}`,
          duration: revealDuration,
          ease: Power2.easeOut,
        },
        textDelay
      )
      .reverseOut();

    this.bottomText.innerControls.y -= boxHeight / 2;
    this.bottomText.alpha = 0;
    this.animate(this.bottomText.innerControls)
      .to(
        {
          alpha: this.primaryColor.getAlpha(),
          y: `+=${boxHeight / 2}`,
          duration: revealDuration,
          ease: Power2.easeOut,
        },
        textDelay
      )
      .reverseOut();

    // just skew it!
    this.zoomableContainer.setTransform(0, 0, 0, 0, 0, 0, -0.1);

    // add zoom effect
    this.zoomableContainer.scale.x = 0.8;
    this.zoomableContainer.scale.y = 0.8;
    this.animate(this.zoomableContainer.scale)
      .to(
        {
          x: 1,
          y: 1,
          duration: zoomDuration,
          ease: Power2.easeInOut,
        },
        revealDuration
      )
      .reverseOut();
  }

  render() {
    super.render();
    this.zoomedGraphics.clear();
    this.gradientBox.draw(this.zoomedGraphics);
    this.topTextMask.render();
    this.bottomTextMask.render();
  }
}
